/**
 * EmailGate - Email Capture Component for Citrus Doctor
 * 
 * Why this matters: Captures lead information before showing full diagnosis results.
 * Sends data to Klaviyo for follow-up email sequences about citrus care.
 */

import React, { useState } from 'react';
import { Mail, Lock, Sparkles } from 'lucide-react';
import { DiagnosisResult } from './CitrusDoctor';

interface EmailGateProps {
  diagnosis: DiagnosisResult;
  onSubmit: (email: string) => void;
  onBack: () => void;
  isLoading: boolean;
  error: string | null;
}

const EmailGate: React.FC<EmailGateProps> = ({
  diagnosis,
  onSubmit,
  onBack,
  isLoading,
  error
}) => {
  const [email, setEmail] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  /**
   * Email validation with stricter regex
   * Why this matters: Prevents obviously invalid emails like test@domain..com
   */
  const isValidEmail = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
  const canSubmit = isValidEmail && agreedToTerms && !isLoading;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (canSubmit) {
      onSubmit(email);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && canSubmit) {
      onSubmit(email);
    }
  };

  // Get severity color for background accent
  const getSeverityAccent = () => {
    if (diagnosis.issues.some(i => i.severity === 'high')) return 'accent-severe';
    if (diagnosis.issues.some(i => i.severity === 'medium')) return 'accent-moderate';
    return 'accent-healthy';
  };

  return (
    <div className="doctor-step doctor-step-email">
      {/* Background visual with diagnosis preview */}
      <div className="email-gate-background">
        <div className={`email-gate-accent ${getSeverityAccent()}`} />
        
        {/* Blurred preview cards */}
        <div className="email-preview-cards">
          <div className="preview-card-blurred">
            <div className="blur-line blur-line-title" />
            <div className="blur-line blur-line-text" />
            <div className="blur-line blur-line-text short" />
          </div>
          <div className="preview-card-blurred">
            <div className="blur-line blur-line-title" />
            <div className="blur-line blur-line-text" />
            <div className="blur-line blur-line-text" />
          </div>
        </div>
      </div>

      {/* Email capture modal */}
      <div className="email-gate-modal">
        <div className="email-gate-header">
          <div className="email-gate-icon">
            <Sparkles size={24} />
          </div>
          <span className="email-gate-label">Your diagnosis is ready!</span>
          <h2 className="email-gate-title">
            Where should we send your full report?
          </h2>
          <p className="email-gate-subtitle">
            Get detailed recommendations plus expert care tips from Dr. Mani.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="email-gate-form">
          <div className="email-input-wrapper">
            <Mail size={20} className="email-input-icon" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="your@email.com"
              className="email-gate-input"
              autoFocus
              disabled={isLoading}
            />
          </div>

          <label className="email-terms">
            <input
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              disabled={isLoading}
            />
            <span>
              I agree to receive my diagnosis report and citrus care tips. View our{' '}
              <a 
                href="https://uscitrusnursery.com/policies/privacy-policy" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>.
            </span>
          </label>

          {error && (
            <div className="email-gate-error">{error}</div>
          )}

          <button
            type="submit"
            className="doctor-btn doctor-btn-primary email-submit-btn"
            disabled={!canSubmit}
          >
            {isLoading ? (
              <span className="loading-text">
                <span className="loading-spinner-small" />
                Preparing your report...
              </span>
            ) : (
              'Get My Full Report'
            )}
          </button>
        </form>

        {/* Report Includes */}
        <div className="email-report-includes">
          <span className="report-includes-title">Your Full Report Includes</span>
          <div className="report-includes-list">
            <span>✅ {diagnosis.recommendations.length} action steps</span>
            <span>🧪 Product recommendations</span>
            <span>🌱 Expert care guide</span>
          </div>
        </div>

        {/* Trust signals */}
        <div className="email-trust-signals">
          <div className="trust-signal">
            <Lock size={14} />
            <span>Your data is secure</span>
          </div>
          <div className="trust-signal">
            <span>No spam, ever</span>
          </div>
          <div className="trust-signal">
            <span>Unsubscribe anytime</span>
          </div>
        </div>

        {/* Navigation */}
        <div className="email-gate-navigation">
          <button
            className="doctor-btn doctor-btn-back"
            onClick={onBack}
            disabled={isLoading}
          >
            ← Back to Preview
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailGate;
